import AttachFileIcon from "@mui/icons-material/AttachFile"
import { Box, Chip, Typography, styled } from "@mui/material"
import type React from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { extraColors } from "../../utils/theme"
import { AgentResponse } from "./ThoughtChain"
import type { ChatMessage } from "./types"

const UserBox = styled(Box)(() => ({
  width: "100%",
  padding: "20px 24px",
  backgroundColor: "rgba(255, 255, 255, 0.04)", // Very soft, translucent white
  borderRadius: "16px",
  marginBottom: "24px",
  color: extraColors.white,
  "& p": { margin: 0 },
}))

const AssistantBox = styled(Box)(() => ({
  width: "100%",
  padding: "0 24px",
  backgroundColor: "transparent",
  color: extraColors.white,
  marginBottom: "40px",
}))

interface MessageListProps {
  messages: ChatMessage[]
}

const AgenticReasoningChain = ({ msg }: { msg: ChatMessage }) => {
  return (
    <AssistantBox>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 1,
          pl: 2,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: extraColors.status.greenLight,
            fontWeight: "bold",
          }}
        >
          Wingman
        </Typography>
        <Typography variant="caption" sx={{ color: extraColors.medium }}>
          {formatTime(msg.timestamp)}
        </Typography>
      </Box>
      <AgentResponse parts={msg.parts ?? []} isComplete={msg.isComplete} />
    </AssistantBox>
  )
}

const AgentSummaryMessage = ({ msg }: { msg: ChatMessage }) => {
  return (
    <AssistantBox>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 1,
          pl: 2,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: extraColors.status.greenLight,
            fontWeight: "bold",
          }}
        >
          Wingman
        </Typography>
        <Typography variant="caption" sx={{ color: extraColors.medium }}>
          {formatTime(msg.timestamp)}
        </Typography>
      </Box>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
    </AssistantBox>
  )
}

const UserPromptMessage = ({ msg }: { msg: ChatMessage }) => {
  return (
    <UserBox>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography
          variant="caption"
          sx={{ color: extraColors.medium, fontWeight: "bold" }}
        >
          You
        </Typography>
        <Typography variant="caption" sx={{ color: extraColors.medium }}>
          {formatTime(msg.timestamp)}
        </Typography>
      </Box>
      <Box sx={{ overflow: "auto", "& pre": { overflow: "auto" } }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
      </Box>
      {msg.attachments && msg.attachments.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
          {msg.attachments.map((name, idx) => (
            <Chip
              key={`${name}-${idx}`}
              icon={
                <AttachFileIcon
                  sx={{ fontSize: "14px !important", color: "inherit" }}
                />
              }
              label={name}
              size="small"
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.08)",
                color: extraColors.medium,
                borderRadius: "4px",
                border: "none",
                "& .MuiChip-icon": { color: extraColors.medium },
              }}
            />
          ))}
        </Box>
      )}
    </UserBox>
  )
}

const formatTime = (isoString?: string) => {
  if (!isoString) return ""
  try {
    const d = new Date(isoString)
    if (isNaN(d.getTime())) return ""
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  } catch {
    return ""
  }
}

export const MessageList: React.FC<MessageListProps> = ({ messages }) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {messages.map(msg => {
        const isAssistant = msg.role === "assistant" || msg.role === "model"
        const messageKey =
          msg.parts?.[0]?.part_id ||
          msg.id ||
          `${msg.role}-${msg.timestamp || "unknown"}`

        if (isAssistant && msg.parts && msg.parts.length > 0) {
          return <AgenticReasoningChain key={messageKey} msg={msg} />
        }

        if (isAssistant) {
          return <AgentSummaryMessage key={messageKey} msg={msg} />
        }

        return <UserPromptMessage key={messageKey} msg={msg} />
      })}
    </Box>
  )
}
